import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import EnrollmentService from "../../_services/EnrollmentService";
import { useEnrollmentSelection } from "./components/useEnrollmentSelection";
import { headerCellStyle, bodyCellStyle } from "./components/SelectableEnrollmentTable";

interface BulkHealthDetailsModalProps {
  show: boolean;
  onHide: () => void;
  selectedEnquiryIds: number[];
  enquiryEnrollmentsMap: Record<number, any[]>;
  studentList: any[];
  onSuccess: () => void;
}

type HealthRow = {
  enrollmentId: number;
  id?: number | null;
  studentCode?: string | null;
  studentName?: string | null;
  height: string;
  weight: string;
  bloodGroup: string;
  leftVision: string;
  rightVision: string;
  actualBMI: string;
  standardBMI: string;
  dentalHygiene: string;
  physicianName: string;
  nurseName: string;
  allergies: string;
  prescribedMedication: string;
  problemDiagnosed: string;
};

// Editable columns in the exact legacy order and with the exact legacy labels.
const HEALTH_COLS: Array<{
  key: keyof HealthRow;
  label: string;
  control: "number" | "text" | "textarea";
}> = [
  { key: "height", label: "Height", control: "number" },
  { key: "weight", label: "Weight", control: "number" },
  { key: "bloodGroup", label: "Blood Group", control: "text" },
  { key: "leftVision", label: "Left Vision", control: "number" },
  { key: "rightVision", label: "Right Vision", control: "number" },
  { key: "actualBMI", label: "Actual BMI", control: "number" },
  { key: "standardBMI", label: "Standard BMI", control: "number" },
  { key: "dentalHygiene", label: "Dental Hygiene", control: "text" },
  { key: "physicianName", label: "Physician Name", control: "text" },
  { key: "nurseName", label: "Nurse Name", control: "text" },
  { key: "allergies", label: "Allergies", control: "textarea" },
  { key: "prescribedMedication", label: "Prescribed Medication", control: "textarea" },
  { key: "problemDiagnosed", label: "Problem Diagnosed", control: "textarea" },
];

const emptyRow = (enrollmentId: number, studentCode?: string | null, studentName?: string | null): HealthRow => ({
  enrollmentId,
  id: null,
  studentCode: studentCode ?? "",
  studentName: studentName ?? "",
  height: "",
  weight: "",
  bloodGroup: "",
  leftVision: "",
  rightVision: "",
  actualBMI: "",
  standardBMI: "",
  dentalHygiene: "",
  physicianName: "",
  nurseName: "",
  allergies: "",
  prescribedMedication: "",
  problemDiagnosed: "",
});

const str = (v: any) => (v === undefined || v === null ? "" : String(v));

const inputStyle: React.CSSProperties = {
  width: 110,
  height: 28,
  padding: "0 6px",
  fontSize: "0.8rem",
  border: "1px solid var(--border-strong)",
  borderRadius: 4,
};

const BulkHealthDetailsModal: React.FC<BulkHealthDetailsModalProps> = ({
  show,
  onHide,
  selectedEnquiryIds,
  enquiryEnrollmentsMap,
  studentList,
  onSuccess,
}) => {
  const { enrollments } = useEnrollmentSelection(
    selectedEnquiryIds,
    enquiryEnrollmentsMap,
    studentList,
    show,
  );

  const [consultationDate, setConsultationDate] = useState("");
  const [rows, setRows] = useState<Record<number, HealthRow>>({});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [savedCount, setSavedCount] = useState<number | null>(null);

  const enrollmentKey = useMemo(
    () => enrollments.map((e) => e.enrollmentId).join(","),
    [enrollments],
  );

  // On open, seed blank rows then pre-fill from existing health records.
  useEffect(() => {
    if (!show) return;
    let cancelled = false;

    const seeded: Record<number, HealthRow> = {};
    for (const e of enrollments) {
      seeded[e.enrollmentId] = emptyRow(e.enrollmentId, e.studentCode, e.studentName);
    }
    setRows(seeded);
    setSavedCount(null);
    setError("");

    if (enrollments.length === 0) return;

    setLoading(true);
    EnrollmentService.getHealthDetails(enrollments.map((e) => e.enrollmentId))
      .then((resp) => {
        if (cancelled) return;
        const records: any[] = resp.data?.data?.records || [];
        const next: Record<number, HealthRow> = { ...seeded };
        let seededDate = "";
        for (const rec of records) {
          const eid = Number(rec.enrollmentId);
          if (!next[eid]) continue;
          next[eid] = {
            enrollmentId: eid,
            id: rec.id ?? null,
            studentCode: rec.studentCode ?? next[eid].studentCode,
            studentName: rec.studentName ?? next[eid].studentName,
            height: str(rec.height),
            weight: str(rec.weight),
            bloodGroup: str(rec.bloodGroup),
            leftVision: str(rec.leftVision),
            rightVision: str(rec.rightVision),
            actualBMI: str(rec.actualBMI),
            standardBMI: str(rec.standardBMI),
            dentalHygiene: str(rec.dentalHygiene),
            physicianName: str(rec.physicianName),
            nurseName: str(rec.nurseName),
            allergies: str(rec.allergies),
            prescribedMedication: str(rec.prescribedMedication),
            problemDiagnosed: str(rec.problemDiagnosed),
          };
          if (!seededDate && rec.consultationDate) {
            seededDate = String(rec.consultationDate).slice(0, 10);
          }
        }
        setRows(next);
        if (seededDate) setConsultationDate(seededDate);
      })
      .catch((err: any) => {
        if (!cancelled) setError(err?.response?.data?.message || "Failed to load health details");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, enrollmentKey]);

  const setField = (enrollmentId: number, key: keyof HealthRow, value: string) => {
    setRows((prev) => ({
      ...prev,
      [enrollmentId]: { ...prev[enrollmentId], [key]: value },
    }));
  };

  const handleClose = () => {
    setConsultationDate("");
    setRows({});
    setError("");
    setSavedCount(null);
    onHide();
  };

  const handleSubmit = async () => {
    setError("");
    if (!consultationDate) {
      setError("Date cannot be blank.");
      return;
    }
    const records = enrollments.map((e) => {
      const r = rows[e.enrollmentId] || emptyRow(e.enrollmentId, e.studentCode, e.studentName);
      return {
        enrollmentId: e.enrollmentId,
        id: r.id || null,
        studentName: r.studentName || "",
        height: r.height,
        weight: r.weight,
        bloodGroup: r.bloodGroup,
        leftVision: r.leftVision,
        rightVision: r.rightVision,
        actualBMI: r.actualBMI,
        standardBMI: r.standardBMI,
        dentalHygiene: r.dentalHygiene,
        physicianName: r.physicianName,
        nurseName: r.nurseName,
        allergies: r.allergies,
        prescribedMedication: r.prescribedMedication,
        problemDiagnosed: r.problemDiagnosed,
      };
    });

    setSubmitting(true);
    try {
      const resp = await EnrollmentService.bulkHealthDetails({ consultationDate, records });
      setSavedCount(resp.data?.data?.saved ?? 0);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "Failed to save health details");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDoneAfterSave = () => {
    handleClose();
    onSuccess();
  };

  return (
    <Modal show={show} onHide={handleClose} size="xl" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 16 }}>
          {savedCount !== null ? "Bulk Health Records — Saved" : "Bulk Health Records"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && (
          <div
            style={{
              padding: "8px 12px",
              background: "var(--status-danger-bg)",
              color: "var(--status-danger)",
              borderRadius: 6,
              marginBottom: 12,
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        {savedCount !== null ? (
          <div style={{ fontSize: 14, color: "var(--text-strong)" }}>
            Students Health details saved/updated successfully —{" "}
            <strong>{savedCount}</strong> record{savedCount === 1 ? "" : "s"} written.
          </div>
        ) : loading ? (
          <div style={{ textAlign: "center", padding: 20, color: "var(--text-muted)" }}>
            <i className="fa fa-spinner fa-spin" /> Loading…
          </div>
        ) : (
          <>
            <Form.Group className="mb-3" style={{ maxWidth: 220 }}>
              <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>
                Date <span style={{ color: "var(--status-danger)" }}>*</span>
              </Form.Label>
              <Form.Control
                type="date"
                size="sm"
                value={consultationDate}
                onChange={(e) => setConsultationDate(e.target.value)}
              />
            </Form.Group>

            {enrollments.length === 0 ? (
              <div
                style={{
                  padding: 16,
                  background: "#fefce8",
                  color: "#854d0e",
                  borderRadius: 6,
                  fontSize: 13,
                }}
              >
                The selected students have no enrollments. Close this dialog and pick rows
                that have at least one enrollment.
              </div>
            ) : (
              <div
                style={{
                  maxHeight: 420,
                  overflow: "auto",
                  border: "1px solid var(--border-default)",
                  borderRadius: 6,
                }}
              >
                <table style={{ borderCollapse: "collapse", whiteSpace: "nowrap" }}>
                  <thead>
                    <tr>
                      <th style={headerCellStyle}>Student Code</th>
                      <th style={headerCellStyle}>Student Name</th>
                      {HEALTH_COLS.map((c) => (
                        <th key={c.key} style={headerCellStyle}>
                          {c.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {enrollments.map((e) => {
                      const r = rows[e.enrollmentId] || emptyRow(e.enrollmentId);
                      return (
                        <tr key={e.enrollmentId}>
                          <td style={bodyCellStyle}>
                            <span style={{ fontWeight: 600 }}>{r.studentCode || "—"}</span>
                          </td>
                          <td style={bodyCellStyle}>
                            <span style={{ color: "var(--text-strong)" }}>
                              {r.studentName || e.studentName}
                            </span>
                          </td>
                          {HEALTH_COLS.map((c) => (
                            <td key={c.key} style={bodyCellStyle}>
                              {c.control === "textarea" ? (
                                <textarea
                                  value={str(r[c.key])}
                                  onChange={(ev) =>
                                    setField(e.enrollmentId, c.key, ev.target.value)
                                  }
                                  rows={2}
                                  style={{
                                    ...inputStyle,
                                    width: 160,
                                    height: 44,
                                    padding: "4px 6px",
                                    resize: "vertical",
                                  }}
                                />
                              ) : (
                                <input
                                  type={c.control === "number" ? "number" : "text"}
                                  value={str(r[c.key])}
                                  onChange={(ev) =>
                                    setField(e.enrollmentId, c.key, ev.target.value)
                                  }
                                  style={inputStyle}
                                />
                              )}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        {savedCount !== null ? (
          <Button variant="primary" onClick={handleDoneAfterSave}>
            Done
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={handleClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={submitting || loading || enrollments.length === 0}
            >
              {submitting ? (
                <>
                  <i className="fa fa-spinner fa-spin" /> Saving…
                </>
              ) : (
                <>SAVE</>
              )}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default BulkHealthDetailsModal;
